import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { QuestionnaireDefinition } from '@/types/scheduling-api';

/**
 * POST /api/Questionnaires/PatientQuestionnaire
 *
 * Mirrors Flutter `SchedulingDataSourceImpl.getQuestionnaire`
 * (`scheduling_data_source_impl.dart:389-413`). Fetches the full
 * questionnaire definition (questions, answer options, recursive Group
 * structure) for the editable form in the Forms tab (PROD-845 PR-2).
 *
 * Wraps `useQuery` even though the underlying HTTP verb is POST — the
 * response is a deterministic function of `(practitionerId, practiceId,
 * formId)`, so cache-by-key works the same as a GET. Saves a refetch
 * when the user closes and reopens the same form.
 *
 * Wire-shape quirk: Flutter hand-builds the JSON body as a string at
 * `:392` rather than using `toJson()`. We use the standard
 * `JSON.stringify` path through `post()`; the resulting body is
 * identical (`{"practitionerId":"...","practiceId":"...","formId":"..."}`).
 *
 * Forwards `signal` per project convention.
 *
 * Enabled only when all three params are present. The Forms tab
 * consumer passes the practitioner / practice from the appointment
 * (`appointment.participant[...].provider.id`,
 * `appointment.practiceId`) and the `formId` from
 * `appointment.questionnaireForm.questionnaireId`.
 */

export interface UseQuestionnaireDefinitionParams {
  practitionerId: string | null | undefined;
  practiceId: string | null | undefined;
  formId: string | null | undefined;
  enabled?: boolean;
}

export function useQuestionnaireDefinition(
  params: UseQuestionnaireDefinitionParams,
): UseQueryResult<QuestionnaireDefinition> {
  const { practitionerId, practiceId, formId, enabled = true } = params;

  // Query-key invariant (Samer PR #262 review §3):
  //   - All three params appear in the key so a change to ANY of them
  //     forces a fresh cache entry + cancels the in-flight request
  //     for the old key. AbortSignal forwarding (project-wide rule)
  //     ensures the old request doesn't race a stale response into
  //     the new key's cache.
  //   - `enabled` gates on all three being truthy so the queryFn is
  //     never invoked with partial params.
  // The runtime guard inside the queryFn is defensive — if React
  // Query somehow fires it with a nullish param, throw a loud error
  // instead of silently sending an empty-string GUID to the backend.
  return useQuery<QuestionnaireDefinition>({
    queryKey: ['questionnaire-definition', practitionerId, practiceId, formId],
    enabled: enabled && !!practitionerId && !!practiceId && !!formId,
    queryFn: ({ signal }) => {
      if (!practitionerId || !practiceId || !formId) {
        // Should be unreachable — `enabled` guard above prevents this.
        // Throw rather than silently POST `{practitionerId: null, ...}`
        // which the backend would 400 on and surface as a confusing
        // QueryClient global-error toast.
        throw new Error(
          'useQuestionnaireDefinition: queryFn invoked with missing params ' +
            `(practitionerId=${practitionerId}, practiceId=${practiceId}, formId=${formId}). ` +
            'Enabled gate failed — this is a bug.',
        );
      }
      return post<QuestionnaireDefinition>(
        '/api/Questionnaires/PatientQuestionnaire',
        { practitionerId, practiceId, formId },
        { signal },
      );
    },
  });
}
